// The booking panel's shell pieces — hero band, "Continuing from X" banner and
// the "In your visit" summary. Moved from ServicePanel.tsx with zero behaviour
// change so a second panel renders the same shell.

import { totalAfterLinkPromo, type LinkPromoPreview } from "../../state/link-promo";
import { formatDue } from "../../state/checkout-steps";
import type { CSSProperties, ReactNode } from "react";
import type { WidgetService } from "../../types";
import { lineGrossCents, type CartItem } from "../../state/cart";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../../lib/image";

// The caller guards with `{continuingFromServiceName && <ContinuingBanner />}`
// so the conditional stays directly under the panel's <section>, as it was
// before the move (see HoldErrorBanner for why that placement matters).
export function ContinuingBanner({
  continuingFromServiceName,
  onDismissContinuing,
}: {
  continuingFromServiceName: string;
  onDismissContinuing?: () => void;
}) {
  return (
    <div className="vt-continuing-banner" role="status">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path d="M5 12l5 5L20 7" />
      </svg>
      <span>
        Continuing from <strong>{continuingFromServiceName}</strong> —
        building up your visit.
      </span>
      {onDismissContinuing && (
        <button
          type="button"
          className="vt-continuing-dismiss"
          onClick={onDismissContinuing}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function ServiceHero({
  service,
  heroImage,
  heroStyle,
  heroHoursPill,
  capacityRemainingText,
  heroPricePill,
  cta,
}: {
  service: Pick<WidgetService, "name" | "tagline" | "service_type">;
  heroImage: string | null;
  heroStyle?: CSSProperties;
  heroHoursPill: { label: string; isOpen: boolean } | null;
  capacityRemainingText: string | null;
  heroPricePill: string | null;
  /** Rendered where ServicePanel's hero CTA sits (after the pills). */
  cta: ReactNode;
}) {
  return (
      <div className="vt-hero" style={heroStyle}>
        {heroImage ? (
          <img
              src={optimizedImageUrl(heroImage, { width: 1280 })}
              srcSet={optimizedSrcSet(heroImage, IMG_WIDTHS.HERO)}
              sizes="100vw"
              alt={service.name}
              decoding="async"
            />
        ) : (
          <div className={`vt-hero-gradient vt-hero-${service.service_type}`} />
        )}
        <div className="vt-hero-overlay">
          <div className="vt-hero-overlay-text">
            <h2>{service.name}</h2>
            {service.tagline && (
              <p className="vt-hero-tag">{service.tagline}</p>
            )}
          </div>
          {/* Hours + capacity pills INLINE on the title row (between the name
              and the right-side meta/CTA), per review. */}
          {(heroHoursPill || capacityRemainingText || heroPricePill) && (
            <div className="vt-hero-pills">
              {heroHoursPill && (
                <div
                  className={`vt-hours-pill${heroHoursPill.isOpen ? "" : " vt-hours-pill-closed"}`}
                  role="status"
                >
                  <span className="vt-hours-pill-dot" aria-hidden="true" />
                  {heroHoursPill.label}
                </div>
              )}
              {capacityRemainingText && (
                <div className="vt-capacity-hint vt-capacity-hint-inline">
                  {capacityRemainingText}
                </div>
              )}
              {heroPricePill && (
                <div className="vt-hero-price-pill">{heroPricePill}</div>
              )}
            </div>
          )}
          {cta}
        </div>
      </div>
  );
}

// What this service already has in the cart — surfaced on the card so the
// guest sees their selection (and the CTA reads as "add another") without
// opening the cart drawer. A plain function, not a component: callers test
// the result for null (`inCartSummary || capacityWarning`).
export function renderInCartSummary(
  cartItems: CartItem[],
  serviceId: string,
  /** The booking link's promo code once checked (useLinkPromoPreview). */
  linkPromo: LinkPromoPreview | null = null,
) {
  const inCart = cartItems.filter((i) => i.serviceId === serviceId);
  // The drawer's money rule, not a second one: unitPrice is a display price
  // and `quantity` on an accommodation line is ROOMS, so the old
  // unitPrice x quantity showed a rack rate for a single night where the
  // drawer showed the real stay total.
  const inCartTotalCents = inCart.reduce(
    (n, i) => n + (lineGrossCents(i) ?? 0),
    0,
  );
  const inCartCurrency = inCart[0]?.currency ?? "ZAR";
  // After the link's code, only where this total is what the checkout
  // charges now (the visit is this service alone, paid in full).
  const after = totalAfterLinkPromo(inCartTotalCents, linkPromo, { serviceId });
  return inCart.length > 0 ? (
      <div className="vt-in-cart" role="status">
        <span className="vt-in-cart-label">
          In your visit · {inCart.length}
        </span>
        <span className="vt-in-cart-items">
          {inCart.map((i) => i.unitName ?? i.description).join(" · ")}
        </span>
        {inCartTotalCents > 0 && (
          <strong className="vt-in-cart-total">
            {after !== null ? (
              <>
                <s className="vt-promo-was">
                  <span className="vt-sr-only">Was </span>
                  {formatDue(inCartCurrency, inCartTotalCents)}
                </s>{" "}
                {formatDue(inCartCurrency, after)}
                <span className="vt-sr-only"> with {linkPromo!.code}</span>
              </>
            ) : (
              <>
                {inCartCurrency} {(inCartTotalCents / 100).toLocaleString()}
              </>
            )}
          </strong>
        )}
      </div>
    ) : null;
}
